import { defineMetrics } from "../types.js"

export const usersMetrics = defineMetrics([
  {
    id: "last_login_date",
    name: "Last Login Date",
    description: "The most recent date and time a user accessed the platform.",
    logic: "Retrieve the latest successful login timestamp for each user.",
    source: "user.lastLogin.timestamp",
    domain: "users",
    srvModule: "users",
    priority: "must",
    valueType: "timestamp",
    dims: ["user"],
    rollup: "max",
    defaultWidget: "table",
    coveredByV1: true,
    sensitive: true,
    xlsxRow: 1,
  },
  {
    id: "username",
    name: "Username",
    description: "User's unique username.",
    logic: "Retrieve from the Users table.",
    source: "user.name",
    domain: "users",
    srvModule: "users",
    priority: "must",
    valueType: "text",
    dims: ["user"],
    rollup: "last",
    defaultWidget: "table",
    coveredByV1: true,
    sensitive: true,
    xlsxRow: 4,
  },
  {
    id: "email_address",
    name: "Email Address",
    description: "User's registered email address.",
    logic: "Retrieve from the Users table.",
    source: "user.email",
    domain: "users",
    srvModule: "users",
    priority: "must",
    valueType: "text",
    dims: ["user"],
    rollup: "last",
    defaultWidget: "table",
    coveredByV1: true,
    sensitive: true,
    xlsxRow: 5,
  },
  {
    id: "user_role",
    name: "User Role",
    description: "Assigned security role or permission level.",
    logic: "Retrieve current assigned role from our system.",
    source: "user.aclRole \u2192 role.name, falling back to the user.role account type",
    domain: "users",
    srvModule: "users",
    priority: "must",
    valueType: "count",
    dims: ["class"],
    rollup: "last",
    defaultWidget: "pie",
    altWidgets: ["hbar", "table"],
    coveredByV1: true,
    xlsxRow: 6,
  },
  {
    id: "user_role_name",
    name: "Assigned Role",
    description: "The security role or permission level assigned to each user.",
    logic: "Retrieve the assigned role name per user, falling back to the account type when none is assigned.",
    source: "user.aclRole → role.name, falling back to the user.role account type",
    domain: "users",
    srvModule: "users",
    priority: "must",
    valueType: "text",
    dims: ["user"],
    rollup: "last",
    defaultWidget: "table",
    coveredByV1: false,
    sensitive: true,
    xlsxRow: 91,
  },
  {
    id: "users_added",
    name: "Users Added",
    description: "Number of new users created during the reporting period.",
    logic: "Count user accounts with a creation date within the selected period.",
    domain: "users",
    srvModule: "users",
    priority: "must",
    valueType: "count",
    dims: [],
    rollup: "sum",
    defaultWidget: "bar",
    altWidgets: ["line", "stat-tile"],
    coveredByV1: true,
    xlsxRow: 7,
  },
  {
    id: "user_deactivated",
    name: "User Deactivated",
    description: "Number of users that have a Deactive Role assigned",
    logic: "Count user account with Deactive Role assigned",
    source: "none \u2014 srv has no deactivation state, see the caveat",
    domain: "users",
    srvModule: "users",
    priority: "unspecified",
    valueType: "count",
    dims: [],
    rollup: "sum",
    defaultWidget: "bar",
    altWidgets: ["stat-tile"],
    coveredByV1: false,
    caveat:
      "srv has no deactivation state — the user document has no status field and there is no Deactive role anywhere in the product, which fits the dictionary leaving both priority and logic blank for this row. Reported as unsupported rather than aliased to deleted accounts, which Users Removed already counts (verified in srv S-2).",
    xlsxRow: 8,
  },
  {
    id: "users_removed",
    name: "Users Removed",
    description: "Number of users removed",
    logic: "Count user accounts deleted",
    source: "user(deleted=true), bucketed by updatedAt \u2014 approximate",
    domain: "users",
    srvModule: "users",
    priority: "must",
    valueType: "count",
    dims: [],
    rollup: "sum",
    defaultWidget: "bar",
    altWidgets: ["line", "stat-tile"],
    coveredByV1: false,
    approximate: true,
    caveat:
      "srv does not record a deletion timestamp, so the period comes from the account's updatedAt. The soft delete is the last write a removed account receives, so the two normally coincide; a later touch such as a migration would move the account into a later period.",
    xlsxRow: 9,
  },
  {
    id: "user_classification",
    name: "User Classification",
    description: "Categorizes users based on their engagement level.",
    logic:
      "Power User (≥X logins/week and ≥Y modules used) Regular User (mid-range)\nOccasional User (low activity)\nDormant (no activity in 30+ days).",
    source: "none yet \u2014 needs the activity and moduleUsage inputs, see the caveat",
    domain: "users",
    srvModule: "users",
    priority: "must",
    valueType: "count",
    dims: ["class"],
    rollup: "last",
    defaultWidget: "pie",
    altWidgets: ["stacked-bar", "hbar", "table"],
    coveredByV1: false,
    caveat:
      "The inputs are now available — srv S-3 landed login_frequency and the per-module usage metrics — but the dictionary's thresholds X and Y are still blank, so the four bands cannot be drawn. This is the only thing missing (tracked as T-22). Until they are named it stays unsupported: a two-slice pie labelled as a four-class metric would misstate it.",
    xlsxRow: 10,
  },
  {
    id: "users_with_module_access",
    name: "Users with Module Access",
    description: "Number of users who have permission to access a specific module.",
    logic: "Count users assigned access to the module through permissions or roles.",
    source: "user.permission[], grouped on whatever strings the deployment stores",
    domain: "users",
    srvModule: "users",
    priority: "must",
    valueType: "count",
    dims: ["module"],
    rollup: "last",
    defaultWidget: "bar",
    altWidgets: ["hbar", "table"],
    coveredByV1: false,
    xlsxRow: 11,
  },
])
